import { SelectProps } from 'antd';
import { get } from 'lodash-es';
import { useCallback } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import FieldWithLabel from '../../../common/components/Fields/FieldWithLabel';
import { FieldProps } from '../../../common/components/Fields/types';
import AsyncSelect from '../../../common/components/Input/AsyncSelect';
import {
  AdminListBrandsOptionsQuery,
  useAdminListBrandsOptionsLazyQuery,
} from '../graphql/products.generated';

export type BrandOption =
  AdminListBrandsOptionsQuery['adminBrands']['edges']['0']['node'];

type BrandsSelectFieldProps<TFieldValues extends FieldValues> = SelectProps &
  FieldProps & {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    initialOption?: { value: number; label: string };
  };

function BrandsSelectField<TFieldValues extends FieldValues>({
  name,
  label,
  required,
  control,
  hideError,
  onChange,
  ...props
}: BrandsSelectFieldProps<TFieldValues>) {
  const [fetchBrands] = useAdminListBrandsOptionsLazyQuery();

  const fetchItems = useCallback(
    async (v: string) => {
      const { data } = await fetchBrands({
        variables: {
          filter: {
            name: v,
            status: 'ACTIVE',
          },
          pagination: {
            take: 20,
            skip: 0,
          },
        },
      });

      return (data?.adminBrands.edges || []).map((edge) => {
        return {
          label: edge.node.name,
          value: edge.node.id,
          data: edge.node,
        };
      });
    },
    [fetchBrands]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState }) => {
        const error = get(formState.errors, name);
        const errorMessage =
          !hideError && typeof error?.message === 'string'
            ? error.message
            : undefined;
        const status = !hideError && errorMessage ? 'error' : '';

        return (
          <FieldWithLabel label={label} required={required}>
            <AsyncSelect
              initialLoad
              className={'w-full'}
              fetchItems={fetchItems}
              status={status}
              {...field}
              onChange={(v, option) => {
                field.onChange(v);

                onChange?.(v, option);
              }}
              {...props}
            />

            {!hideError && errorMessage && (
              <p className={'text-xs text-red-500'}>{errorMessage}</p>
            )}
          </FieldWithLabel>
        );
      }}
    />
  );
}

export default BrandsSelectField;
